import { useEffect, useState } from 'react';
import { Camera, Loader2, MapPin, Phone, User } from 'lucide-react';
import { ensureCrmOrderMeasurement, getCrmOrderBoardCard, type CrmOrderBoardCard } from '../../../api';
import { AdminGlassDisclosure } from '../AdminGlassDisclosure';
import { CrmMeasurementReportBlock } from './CrmMeasurementReportBlock';
import { CrmOrderComposePanel } from './CrmOrderComposePanel';
import { ORDER_STATUS_LABELS } from './crmLabels';
import { formatAdminPanelError } from '@/lib/adminApiError';

type Props = {
  orderId: number;
  canEditMeasurement: boolean;
  canEditItems: boolean;
  canEditPricing: boolean;
  hapticSelection: () => void;
  hapticSuccess: () => void;
  onUpdated: () => void;
  onOpenCatalog?: () => void;
  onProductionHandoff?: () => void;
  salesStage?: string | null;
  /** Только замер, фото, production_notes — без редактора состава */
  opsOnly?: boolean;
};

export function CrmOrderBoardPanel({
  orderId,
  canEditMeasurement,
  canEditItems,
  canEditPricing,
  hapticSelection,
  hapticSuccess,
  onUpdated,
  onOpenCatalog,
  onProductionHandoff,
  salesStage,
  opsOnly = false,
}: Props) {
  const [data, setData] = useState<CrmOrderBoardCard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creatingMeas, setCreatingMeas] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await getCrmOrderBoardCard(orderId));
    } catch (err: unknown) {
      setError(formatAdminPanelError(err, 'Не удалось загрузить карточку'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [orderId]);

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <Loader2 size={20} className="animate-spin" style={{ color: 'var(--cyan)' }} />
      </div>
    );
  }

  if (error || !data) {
    return (
      <p className="text-xs px-1" style={{ color: '#FC814A' }}>{error || 'Нет данных'}</p>
    );
  }

  const { order, client, measurements } = data;
  const address = order.delivery_address || measurements[0]?.address || measurements[0]?.city;

  return (
    <div className="space-y-2">
      <div
        className="rounded-xl px-3 py-2 grid gap-1 text-[11px]"
        style={{ background: 'rgba(99,179,237,0.06)', border: '1px solid rgba(99,179,237,0.18)' }}
      >
        <p className="text-xs font-bold truncate" style={{ color: 'var(--text-1)' }}>{order.order_number}</p>
        <span className="flex items-center gap-1.5 truncate" style={{ color: 'var(--text-2)' }}>
          <User size={11} className="shrink-0" /> {client.name}
        </span>
        {client.phone && (
          <span className="flex items-center gap-1.5" style={{ color: 'var(--text-3)' }}>
            <Phone size={11} /> {client.phone}
          </span>
        )}
        {address && (
          <span className="flex items-start gap-1.5" style={{ color: 'var(--text-3)' }}>
            <MapPin size={11} className="shrink-0 mt-0.5" /> {address}
          </span>
        )}
        <span style={{ color: 'var(--text-3)' }}>
          {ORDER_STATUS_LABELS[order.status || 'pending'] || order.status}
          {' · '}
          {Math.round(order.final_amount).toLocaleString('ru-RU')} ₽
        </span>
      </div>

      {!opsOnly && (
        <CrmOrderComposePanel
          orderId={orderId}
          orderNumber={order.order_number}
          canEditItems={canEditItems}
          onOpenCatalog={onOpenCatalog}
          onProductionHandoff={onProductionHandoff}
          salesStage={salesStage ?? order.sales_stage}
          canEditPricing={canEditPricing}
          hapticSelection={hapticSelection}
          hapticSuccess={hapticSuccess}
          onUpdated={() => {
            void load();
            onUpdated();
          }}
        />
      )}

      {order.production_notes && (
        <p className="text-[10px] px-2 py-1.5 rounded-lg" style={{ background: 'rgba(159,122,234,0.1)', color: '#D6BCFA' }}>
          Цех: {order.production_notes}
        </p>
      )}

      <AdminGlassDisclosure
        accent="amber"
        density="compact"
        defaultOpen={false}
        title="Замер и фото"
        subtitle={measurements.length ? `${measurements.length} заяв.` : 'Создать заявку'}
        badge={
          (data.attachments?.length ||
            measurements.reduce((n, m) => n + (m.attachment_count ?? 0), 0)) || undefined
        }
        icon={<Camera size={14} color="#F6AD55" />}
      >
        {measurements.length > 0 ? (
          <div className="space-y-2">
            {measurements.map((m) => (
              <CrmMeasurementReportBlock
                key={m.id}
                measurement={m}
                orderId={orderId}
                canEdit={canEditMeasurement}
                hapticSelection={hapticSelection}
                hapticSuccess={hapticSuccess}
                onUpdated={() => {
                  void load();
                  onUpdated();
                }}
              />
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-[10px] leading-relaxed" style={{ color: 'var(--text-3)' }}>
              {opsOnly
                ? 'Состав и скидка — в блоке «Состав и деньги». Здесь замер и отчёт.'
                : 'После замера добавьте позиции выше и переведите сделку в «Производство».'}
            </p>
            {canEditMeasurement && (
              <button
                type="button"
                disabled={creatingMeas}
                onClick={async () => {
                  hapticSelection();
                  setCreatingMeas(true);
                  try {
                    await ensureCrmOrderMeasurement(orderId, {
                      city: data.suggested_measurement?.city || address || undefined,
                      notes: `Замер по ${order.order_number}`,
                    });
                    hapticSuccess();
                    await load();
                    onUpdated();
                  } catch (err: unknown) {
                    setError(formatAdminPanelError(err, 'Не удалось создать замер'));
                  } finally {
                    setCreatingMeas(false);
                  }
                }}
                className="press w-full py-2.5 rounded-xl text-xs font-bold disabled:opacity-50 min-h-[44px]"
                style={{ background: 'rgba(246,173,85,0.18)', color: '#F6AD55', border: '1px solid rgba(246,173,85,0.4)' }}
              >
                {creatingMeas ? 'Создаём…' : 'Запланировать замер'}
              </button>
            )}
          </div>
        )}
      </AdminGlassDisclosure>
    </div>
  );
}
